import { gql, GraphQLClient } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";
import type { LessonCompletionResponse } from "./lesson-completion";

// GraphQL Queries
const GET_USER_QUIZ_PROGRESS = gql`
  query GetUserQuizProgress($lessonId: String!, $courseId: String!) {
    getUserQuizProgress(lessonId: $lessonId, courseId: $courseId) {
      hasCompleted
      isPassed
      bestScore
      lastSessionId
    }
  }
`;

// GraphQL Mutations
const START_QUIZ_SESSION = gql`
  mutation StartQuizSession($lessonId: String!, $courseId: String!) {
    startQuizSession(lessonId: $lessonId, courseId: $courseId) {
      session {
        id
        lessonId
        courseId
        totalQuestions
        answeredQuestions
        correctAnswers
        scorePercentage
        isPassed
        status
        sessionType
        passThreshold
        timeLimit
      }
      questions {
        id
        questionText
        points
        choices {
          id
          choiceText
          orderIndex
        }
      }
    }
  }
`;

const SUBMIT_QUIZ_ANSWER = gql`
  mutation SubmitQuizAnswer(
    $sessionId: String!
    $questionId: String!
    $choiceId: String!
  ) {
    submitQuizAnswer(
      sessionId: $sessionId
      questionId: $questionId
      choiceId: $choiceId
    ) {
      isCorrect
      explanation
      correctChoiceId
    }
  }
`;

const FINISH_QUIZ_SESSION = gql`
  mutation FinishQuizSession($sessionId: String!) {
    finishQuizSession(sessionId: $sessionId) {
      session {
        id
        lessonId
        courseId
        totalQuestions
        answeredQuestions
        correctAnswers
        scorePercentage
        isPassed
        status
        passThreshold
        fieldScores {
          field
          total
          correct
        }
      }
      lessonCompleted
      lessonCompletion {
        success
        message
        completedLessons
        totalLessons
        progressPercentage
        completedCourse
        courseName
        xp
        totalXp
        rank
        rankUp
        previousRank
        level
        levelUp
        previousLevel
      }
    }
  }
`;

// Types
export interface QuizProgress {
  hasCompleted: boolean;
  isPassed: boolean;
  bestScore: number;
  lastSessionId: string | null;
}

export interface QuizSession {
  id: string;
  lessonId: string | null;
  courseId: string;
  totalQuestions: number;
  answeredQuestions: number;
  correctAnswers: number;
  scorePercentage: number;
  isPassed: boolean;
  status: string;
  /** "practice" (通常のクイズ) か "exam" (模試)。startQuizSession のときだけ入る */
  sessionType?: string;
  passThreshold?: number;
  /** 制限時間 (秒)。模試だけ入る。null なら制限時間なし */
  timeLimit?: number | null;
  /** 分野別の正答数。設問に field タグがあるときだけ入る。finishQuizSession のときだけ埋まる */
  fieldScores?: QuizFieldScore[];
}

export interface QuizFieldScore {
  field: string;
  total: number;
  correct: number;
}

export interface SessionQuestion {
  id: string;
  questionText: string;
  points: number;
  choices: Array<{
    id: string;
    choiceText: string;
    orderIndex: number;
  }>;
}

export interface StartQuizSessionResponse {
  session: QuizSession;
  questions: SessionQuestion[];
}

export interface SubmitQuizAnswerResponse {
  isCorrect: boolean;
  explanation: string | null;
  correctChoiceId: string | null;
}

export interface FinishQuizSessionResponse {
  session: QuizSession;
  lessonCompleted: boolean;
  lessonCompletion: LessonCompletionResponse | null;
}

export interface GraphqlQuizProgressOptions extends GraphqlRequestConfig {
  client?: GraphQLClient;
}

function ensureClient(options?: GraphqlQuizProgressOptions) {
  if (options?.client) {
    return options.client;
  }
  return createGraphqlClient(options);
}

// API Functions
export async function getUserQuizProgress(
  lessonId: string,
  courseId: string,
  options?: GraphqlQuizProgressOptions,
): Promise<QuizProgress> {
  const client = ensureClient(options);
  const data = await client.request(GET_USER_QUIZ_PROGRESS, {
    lessonId,
    courseId,
  });
  return (data as any).getUserQuizProgress;
}

export async function startQuizSession(
  lessonId: string,
  courseId: string,
  options?: GraphqlQuizProgressOptions,
): Promise<StartQuizSessionResponse> {
  const client = ensureClient(options);
  const data = await client.request(START_QUIZ_SESSION, { lessonId, courseId });
  return (data as any).startQuizSession;
}

export async function submitQuizAnswer(
  sessionId: string,
  questionId: string,
  choiceId: string,
  options?: GraphqlQuizProgressOptions,
): Promise<SubmitQuizAnswerResponse> {
  const client = ensureClient(options);
  const data = await client.request(SUBMIT_QUIZ_ANSWER, {
    sessionId,
    questionId,
    choiceId,
  });
  return (data as any).submitQuizAnswer;
}

export async function finishQuizSession(
  sessionId: string,
  options?: GraphqlQuizProgressOptions,
): Promise<FinishQuizSessionResponse> {
  const client = ensureClient(options);
  const data = await client.request(FINISH_QUIZ_SESSION, { sessionId });
  return (data as any).finishQuizSession;
}
